import './style/salesDashboard.scss';
import {AwesomeIcon, ChangeFunction} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import React, {KeyboardEventHandler, MouseEventHandler, useCallback, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import Account from '../../models/accountModel';

import {PeriodLabel, SalesCategory, SalesColumn} from '../../util/enums';

import {
    BolmateControlColor,
    GeneralSalesProduct,
    PortalSelectGroup,
    PortalSelectOption,
    SalesDashboardFilterGroup,
    SalesDashboardStats
} from '../../util/types';

import {BolmateButton, BolmatePeriodSelector, BolmateSearchInput, BolmateStatCard} from '../controls';

import BolmateToggleButton from '../controls/button/bolmateToggleButton';

import {AppContext} from '../misc/appContext';

import SalesDashboardTable from './salesDashboardTable';

import BolmateTooltip from '../controls/tooltip/bolmateTooltip';

import PortalSelectController from '../controls/portalSelect/portalSelectController';

interface Props {
    onChangeSearch: (value: string) => void;
    onEnterSearch: () => void;
    onChangeCategoryVisibility: (category: SalesCategory) => (toggled: boolean) => void;
    onChangeColumnVisibility: (value: SalesColumn | null, selected: boolean) => void;
    onExport: MouseEventHandler;
    onChangeCountries: (countryCodes: string) => void;
    onChangeAccounts: (accountID: string | null, selected: boolean, accountIDs: Array<string>) => void;
    onChangePeriod: (range: [IDate, IDate], presetLabel: PeriodLabel | null) => void;
    onChangeOrder: (order: SalesColumn) => MouseEventHandler;
    onClearFilters: MouseEventHandler;
    onApplyFilter: (filterGroup: SalesDashboardFilterGroup) => void;
    onChangePage: (nextPage: number) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    onClickProduct: (product: GeneralSalesProduct) => MouseEventHandler;
    onChangeProductVisibility: (value: boolean) => void;
    search: string;
    countryCodes: string;
    accountIDs: Set<string>;
    period: PeriodLabel;
    customPeriodStart: IDate | null;
    customPeriodEnd: IDate | null;
    page: number;
    limit: number;
    total: number;
    order: SalesColumn;
    filters: Array<SalesDashboardFilterGroup>;
    products: Array<GeneralSalesProduct>;
    loadingProducts: boolean;
    loadingStats: boolean;
    stats: SalesDashboardStats | null;
    comparingStats: SalesDashboardStats | null;
    hiddenCategories: Set<SalesCategory>;
    hiddenColumns: Set<SalesColumn>;
    showHiddenProducts: boolean;
}

const flag = (code: string) => <span className={`fi fi-${code}`} style={{margin: '0 0.375rem'}} />;
const countryItems: Array<PortalSelectOption> = [
    {value: 'NL', label: <>{flag('nl')} NL</>},
    {value: 'BE', label: <>{flag('be')} BE</>},
    {
        value: 'NL,BE',
        label: (
            <>
                {flag('nl')} & {flag('be')}
            </>
        )
    }
];

const visibilityItem = (label: string, letter: string, color: string) => (
    <label className='sales-dashboard-column-select-item'>
        {label} <span className={`column-category ${color}`}>{letter}</span>
    </label>
);

export default function SalesDashboard(props: Props) {
    const {t} = useTranslation();
    const {accounts} = useContext(AppContext).accountContext;
    const columnVisibilityItems: Array<PortalSelectGroup<SalesColumn>> = [
        {
            groupLabel: t('salesDashboardV2.table.sales', 'Verkopen'),
            items: [
                {
                    value: SalesColumn.QUANTITY,
                    label: visibilityItem(t('salesDashboardV2.table.quantitySold', 'Aantal verkocht'), 'S', 'green')
                },
                {
                    value: SalesColumn.REVENUE,
                    label: visibilityItem(t('salesDashboardV2.table.revenue', 'Omzet'), 'S', 'green')
                },
                {
                    value: SalesColumn.ORGANIC_SALES,
                    label: visibilityItem(t('salesDashboardV2.table.organicRevenue', 'Organische omzet'), 'S', 'green')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.returns', 'Retouren'),
            items: [
                {
                    value: SalesColumn.RETURN_QUANTITY,
                    label: visibilityItem(t('salesDashboardV2.table.returnQuantity', 'Aantal retour'), 'R', 'red')
                },
                {
                    value: SalesColumn.RETURN_REVENUE,
                    label: visibilityItem(t('salesDashboardV2.table.returnRevenue', 'Retour omzet'), 'R', 'red')
                },
                {
                    value: SalesColumn.RETURN_COSTS,
                    label: visibilityItem(t('salesDashboardV2.table.returnCosts', 'Retourkosten'), 'R', 'red')
                },
                {
                    value: SalesColumn.RETURN_COMMISSION,
                    label: visibilityItem(t('salesDashboardV2.table.returnCommission', 'Retour commissie'), 'R', 'red')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.advertising', 'Adverteren'),
            items: [
                {
                    value: SalesColumn.AD_SPENT,
                    label: visibilityItem(t('salesDashboardV2.table.adCosts', 'Advertentie kosten'), 'A', 'purple')
                },
                {
                    value: SalesColumn.SPONSORED_SALES,
                    label: visibilityItem(t('salesDashboardV2.table.adRevenue', 'Advertentie omzet'), 'A', 'purple')
                },
                {
                    value: SalesColumn.ACOS,
                    label: visibilityItem('ACoS', 'A', 'purple')
                },
                {
                    value: SalesColumn.CPC,
                    label: visibilityItem('CPC', 'A', 'purple')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.costs', 'Kosten'),
            items: [
                {
                    value: SalesColumn.PROD_COSTS,
                    label: visibilityItem(t('salesDashboardV2.table.productCosts', 'Product kosten'), 'P', 'blue')
                },
                {
                    value: SalesColumn.COMMISSION,
                    label: visibilityItem(t('salesDashboardV2.table.commission', 'Commissie'), 'P', 'blue')
                },
                {
                    value: SalesColumn.VAT,
                    label: visibilityItem(t('salesDashboardV2.table.vat', 'BTW'), 'P', 'blue')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.profit', 'Winst'),
            items: [
                {
                    value: SalesColumn.PROFIT,
                    label: visibilityItem(t('salesDashboardV2.table.profit', 'Winst'), 'W', 'yellow')
                },
                {
                    value: SalesColumn.MARGIN,
                    label: visibilityItem(t('salesDashboardV2.table.marginPct', 'Marge %'), 'W', 'yellow')
                }
            ]
        }
    ];
    const {
        onChangeSearch,
        onEnterSearch,
        onChangeCategoryVisibility,
        onChangeColumnVisibility,
        onChangeCountries,
        onChangeAccounts,
        onChangePeriod,
        onChangeOrder,
        onApplyFilter,
        onChangePage,
        onChangeLimit,
        onClickProduct,
        onChangeProductVisibility,
        showHiddenProducts,
        search,
        countryCodes,
        accountIDs,
        period,
        customPeriodStart,
        customPeriodEnd,
        page,
        limit,
        total,
        order,
        filters,
        products,
        loadingProducts,
        stats,
        comparingStats,
        hiddenCategories,
        hiddenColumns
    } = props;
    const accountItems: Array<PortalSelectOption> = useMemo(
        () =>
            accounts.map((account: Account) => ({
                value: account.id,
                label: account.selectablePlatformLabel
            })),
        [accounts]
    );

    function renderStats() {
        return (
            <div className='stats-container'>
                <BolmateStatCard
                    label={t('salesDashboardV2.numOrders', 'Aantal orders')}
                    value={stats?.num_orders ?? 0}
                    prevValue={comparingStats?.num_orders ?? 0}
                />
                <BolmateStatCard
                    label={t('salesDashboardV2.revenueExcl', 'Omzet excl. BTW')}
                    value={stats?.revenue_excl ?? 0}
                    prevValue={comparingStats?.revenue_excl ?? 0}
                    alternate={{
                        label: t('salesDashboardV2.revenueIncl', 'Omzet incl. BTW'),
                        value: stats?.revenue_incl ?? 0,
                        prevValue: comparingStats?.revenue_incl ?? 0,
                        valueType: 'currency'
                    }}
                    valueType='currency'
                />
                <BolmateStatCard
                    label={t('salesDashboardV2.profitExcl', 'Winst excl. BTW')}
                    value={stats?.profit ?? 0}
                    prevValue={comparingStats?.profit ?? 0}
                    alternate={{
                        label: t('salesDashboardV2.margin', 'Marge'),
                        value: stats?.margin ?? 0,
                        prevValue: comparingStats?.margin ?? 0,
                        valueType: 'percentage'
                    }}
                    valueType='currency'
                />
                <BolmateStatCard
                    label={t('salesDashboardV2.table.organicRevenue', 'Organische omzet')}
                    value={stats?.organic_sales ?? 0}
                    prevValue={comparingStats?.organic_sales ?? 0}
                    alternate={{
                        label: t('salesDashboardV2.table.adRevenue', 'Advertentie omzet'),
                        value: stats?.sponsored_sales ?? 0,
                        prevValue: comparingStats?.sponsored_sales ?? 0,
                        valueType: 'currency'
                    }}
                    valueType='currency'
                />
                <BolmateStatCard
                    label={t('salesDashboardV2.table.returnQuantity', 'Aantal retour')}
                    value={stats?.return_quantity ?? 0}
                    prevValue={comparingStats?.return_quantity ?? 0}
                    invertTrend
                    alternate={{
                        label: t('salesDashboardV2.returnRevenueAlt', 'Omzet retour'),
                        value: stats?.returned_revenue_excl ?? 0,
                        prevValue: comparingStats?.returned_revenue_excl ?? 0,
                        valueType: 'currency',
                        invertTrend: true
                    }}
                />
            </div>
        );
    }

    function renderGlobalFilters() {
        return (
            <div className='global-filters'>
                <div className='control-bar'>
                    <div className='control-bar-left'>{renderSearch()}</div>
                    <div className='control-bar-right'>
                        {renderSelectables()}
                        {renderVisibilityToggles()}
                    </div>
                </div>
            </div>
        );
    }

    const handleEnterSearch = useCallback<KeyboardEventHandler<HTMLInputElement>>(
        e => {
            if (e.key === 'Enter') onEnterSearch();
        },
        [onEnterSearch]
    );

    function renderSearch() {
        return (
            <BolmateSearchInput
                onChange={onChangeSearch}
                onKeyDown={handleEnterSearch}
                value={search}
                placeholder={t('salesDashboardV2.searchPlaceholder', 'Zoek producttitel of EAN...')}
                className='search-field'
                size='small'
                autoFocus
            />
        );
    }

    function renderSelectables() {
        return (
            <>
                <PortalSelectController
                    onChange={onChangeCountries}
                    items={countryItems}
                    shape='rounded'
                    value={countryCodes}
                    size='small'
                    label={t('salesDashboardV2.salesFrom', 'Sales from')}
                />
                <PortalSelectController
                    onChange={onChangeAccounts}
                    items={accountItems}
                    value={Array.from(accountIDs)}
                    shape='rounded'
                    size='small'
                    controls={['selectAll', 'deselectAll']}
                    multiple
                    label={t('salesDashboardV2.shops', 'Winkels')}
                    parseSelectValue={(values: Array<string>) => {
                        if (values.length === 0 || values.length === accountItems.length)
                            return t('salesDashboardV2.allShops', 'Alle winkels');
                        if (values.length === 1) return t('salesDashboardV2.oneShop', '1 winkel');
                        return t('salesDashboardV2.nShops', '{{count}} winkels', {count: values.length});
                    }}
                />
                <BolmatePeriodSelector
                    onChange={onChangePeriod}
                    value={[customPeriodStart, customPeriodEnd]}
                    selectedPreset={period}
                    selectSize='small'
                    selectShape='rounded'
                    label={t('salesDashboardV2.period', 'Periode')}
                />
            </>
        );
    }

    function renderVisibilityToggles() {
        return (
            <>
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.sales', 'Verkopen'),
                    SalesCategory.SALES,
                    'green'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.returns', 'Retouren'),
                    SalesCategory.RETURNS,
                    'red'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.advertising', 'Adverteren'),
                    SalesCategory.ADVERTISING,
                    'purple'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.costs', 'Kosten'),
                    SalesCategory.COSTS,
                    'blue'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.profit', 'Winst'),
                    SalesCategory.PROFIT,
                    'yellow'
                )}
                <div className='separator' />
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.comparison', 'Vergelijking'),
                    SalesCategory.COMPARISONS,
                    'blue'
                )}
                <PortalSelectController
                    onChange={onChangeColumnVisibility}
                    items={columnVisibilityItems}
                    itemsHeight='30rem'
                    shape='rounded'
                    size='small'
                    multiple
                    disableAutoScroll
                    label={t('salesDashboardV2.columns', 'Kolommen')}
                    defaultValue={columnVisibilityItems.reduce(
                        (prev: Array<SalesColumn>, item) => prev.concat(item.items.map(item => item.value)),
                        []
                    )}
                    parseSelectValue={(values: Array<SalesColumn>) => {
                        if (values.length === 1) return t('salesDashboardV2.oneColumn', '1 kolom');
                        return t('salesDashboardV2.nColumns', '{{count}} kolommen', {count: values.length});
                    }}
                    controls={['selectAll', 'deselectAll']}
                />
                <BolmateToggleButton
                    label={t('salesDashboardV2.hiddenProducts', 'Verborgen producten')}
                    color='blue'
                    onToggle={onChangeProductVisibility}
                    active={showHiddenProducts}
                    size='small'
                    shape='rounded'
                    secondary
                    ornaments={{element: <AwesomeIcon name='eye' prefix='far' />}}
                />
                <BolmateTooltip text={t('salesDashboardV2.comingSoon', 'Binnenkort beschikbaar')}>
                    <span>
                        <BolmateButton
                            onClick={() => {}}
                            label={t('salesDashboardV2.export', 'Export')}
                            ornaments={{element: <AwesomeIcon name='file-excel' prefix='far' />}}
                            color='green'
                            size='small'
                            shape='rounded'
                            secondary
                            disabled
                        />
                    </span>
                </BolmateTooltip>
            </>
        );
    }

    function renderCategoryVisibilityToggle(label: string, category: SalesCategory, color: BolmateControlColor) {
        return (
            <BolmateToggleButton
                label={label}
                color={color}
                onToggle={onChangeCategoryVisibility(category)}
                defaultActive={true}
                size='small'
                shape='rounded'
                ornaments={{element: <AwesomeIcon name='eye' prefix='far' />}}
                secondary
            />
        );
    }

    return (
        <div className='sales-dashboard-v2'>
            {renderStats()}
            {renderGlobalFilters()}
            <SalesDashboardTable
                onChangeOrder={onChangeOrder}
                onApplyFilters={onApplyFilter}
                onClickProduct={onClickProduct}
                onChangeLimit={onChangeLimit}
                onChangePage={onChangePage}
                total={total}
                products={products}
                order={order}
                loadingProducts={loadingProducts}
                filters={filters}
                limit={limit}
                page={page}
                hiddenCategories={hiddenCategories}
                hiddenColumns={hiddenColumns}
            />
        </div>
    );
}
